/**
 * {@link useOnlineStatus} — a small React hook that tracks browser
 * connectivity, backing the online/offline indicator shown by the Participant
 * and Score Entry apps (Req 18.5, 19.6).
 *
 * It seeds from `navigator.onLine` and subsequently follows the window
 * `'online'` / `'offline'` events. These events are the platform's only signal
 * for connectivity changes and are trivially driven in jsdom tests by toggling
 * `navigator.onLine` and dispatching the corresponding event.
 *
 * Note: `navigator.onLine === true` only guarantees a local network interface,
 * not reachability of the API. The indicator is therefore a coarse hint; actual
 * request failures are surfaced separately by the API client.
 */
import { useEffect, useState } from 'react';
/** Read `navigator.onLine`, defaulting to `true` when unavailable (SSR/tests). */
function readNavigatorOnline() {
    if (typeof navigator === 'undefined' || typeof navigator.onLine !== 'boolean') {
        return true;
    }
    return navigator.onLine;
}
/**
 * Returns `true` while the browser reports network connectivity, `false`
 * otherwise. Re-renders the consumer whenever connectivity changes.
 */
export function useOnlineStatus() {
    const [online, setOnline] = useState(readNavigatorOnline);
    useEffect(() => {
        const handleOnline = () => setOnline(true);
        const handleOffline = () => setOnline(false);
        // Re-sync on mount in case connectivity changed before listeners attached.
        setOnline(readNavigatorOnline());
        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);
        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, []);
    return online;
}
//# sourceMappingURL=useOnlineStatus.js.map